import { useSuspenseQuery } from '@apollo/client';
import type React from 'react';
import { Suspense, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { useParams } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { PageLayout } from '@xing-com/crate-people-directory-page-layout';
import { Pagination } from '@xing-com/crate-people-directory-pagination';
import { UserChunk } from '@xing-com/crate-people-directory-user-chunk';
import {
  UserMessage,
  UserMessageType,
} from '@xing-com/crate-people-directory-user-message';
import { useMediaListener, BREAKPOINTS } from '@xing-com/util';

import {
  StyledHeadline,
  ContentPlaceholder,
} from './chunk-page-by-city.styles';
import { SEO_URL_META_DATA } from './constants';
import { GetByCityChunkPageDocument } from './graphql/chunk-page-by-city.query.gql-types';
import { sanitizeNames } from './helper';

type BasicPageLayoutProps = {
  seoTitle: string;
  seoDescription: string;
  index: boolean;
  letter: string;
  seoUrl: string;
  pageName: string;
};

const ChunkPageByCityInner: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  const { country, city: citySlug, letter, pageLevel, page } = useParams();
  const { isLoggedOut } = useLoginState();

  const { data, error } = useSuspenseQuery(GetByCityChunkPageDocument, {
    variables: {
      country,
      city: citySlug,
      letter,
      pageLevel: +pageLevel || 1,
      page: +page,
    },
    skip: !letter,
  });
  const { formatMessage } = useIntl();
  const breakpoint = useMediaListener();
  const isDesktop = breakpoint === BREAKPOINTS.desktop;

  useEffect(() => {
    if (!isLoggedOut) {
      window.location.assign('https://www.xing.com/search/members');
    }
  }, [isLoggedOut]);

  if (!isLoggedOut) return;
  if (error || !data) throw error;

  const { names, name, toName, totalPages, city, breadcrumbs } =
    data?.peopleByCityChunkPage || {};

  const sanitizedBreadcrumbs = breadcrumbs?.map((breadcrumb) => {
    return {
      title: breadcrumb?.title || '',
      url: `${breadcrumb?.url}` || '',
    };
  });

  const chunkPageData = {
    country,
    city: citySlug,
    letter,
    pageLevel: +pageLevel || 1,
    page,
  };

  if (!data?.peopleByCityChunkPage || !names || !names.length) {
    return <EmptyStateFallback {...basicPageLayoutProps} />;
  }

  const headlineMessage = `${name} ... ${toName}`;

  const seoTitle = formatMessage(
    { id: 'PD_LETTER_ROOT_TITLE' },
    { display_name: headlineMessage }
  );

  const description = formatMessage(
    { id: 'PD_CITIES_CITY_LETTER_META_DESCRIPTION' },
    { description: headlineMessage, city }
  );
  const fullPageLayoutProps = {
    ...basicPageLayoutProps,
    seoTitle,
    seoDescription: description,
    breadcrumbs: sanitizedBreadcrumbs,
  };

  const route = `/people/cities/:country/${citySlug}/pages/:letter/:pageLevel/:page/`;

  return (
    <PageLayout {...fullPageLayoutProps}>
      <StyledHeadline size={isDesktop ? 'xxlarge' : 'large'}>
        {headlineMessage}
      </StyledHeadline>
      {names ? (
        <UserChunk names={sanitizeNames(names)} fullWidthLayout />
      ) : null}
      {page && totalPages && (
        <Pagination
          currentPage={page}
          pagesNumber={totalPages}
          route={route}
          routeParams={chunkPageData}
        />
      )}
    </PageLayout>
  );
};

const LoadingStateFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  const breakpoint = useMediaListener();
  const isDesktop = breakpoint === BREAKPOINTS.desktop;

  return (
    <PageLayout {...basicPageLayoutProps}>
      <ContentPlaceholder>
        <StyledHeadline size={isDesktop ? 'xxlarge' : 'large'}>
          ...
        </StyledHeadline>
      </ContentPlaceholder>
    </PageLayout>
  );
};

const EmptyStateFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <UserMessage type={UserMessageType.empty} />
    </PageLayout>
  );
};

const ErrorFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <UserMessage type={UserMessageType.error} />
    </PageLayout>
  );
};

export const ChunkPageByCity: React.FC = () => {
  const { formatMessage } = useIntl();
  const { country, city: citySlug, letter, pageLevel, page } = useParams();

  const basicPageLayoutProps = {
    seoTitle: formatMessage({ id: 'PD_100_HEADING' }),
    seoDescription: formatMessage({ id: 'PD_100_DESCR' }),
    index: true,
    letter,
    seoUrl: `${SEO_URL_META_DATA}cities/${country}/${citySlug}/pages/${letter}/${pageLevel}/${page}`,
    pageName: `cities/${country}/${letter}-3`,
  };

  return (
    <ErrorBoundary onError={() => <ErrorFallback {...basicPageLayoutProps} />}>
      <Suspense fallback={<LoadingStateFallback {...basicPageLayoutProps} />}>
        <ChunkPageByCityInner {...basicPageLayoutProps} />
      </Suspense>
    </ErrorBoundary>
  );
};
